import { CSSProperties, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { popup } from '@telegram-apps/sdk-react';
import { useNavigate } from 'react-router-dom';

import { useBackButtonToPrevRoute } from '@/hooks/useBackButtonToPrevRoute.tsx';
import { routes } from '@/navigation/routes.tsx';
import { MainButton } from '@/components/common/MainButton/MainButton.tsx';
import PauseShape from '@/components/common/svg/shapes/PauseShape.tsx';
import ProfileController from '@/api/profile/profile.ts';
import { catchErrors } from '@/utils/sentry-monitor.tsx';
import { useProfileStore } from '@/store/useProfileStore.ts';
import usePolicy from '@/hooks/usePolicy.ts';
import { STORAGE_KEYS } from '@/constants/local_storage_keys.ts';
import { useTooltipStore } from '@/store/useTooltipStore.ts';
import { useCloudStorage } from '@/hooks/useStorage.ts';
import UIConfig from '@/UIConfig.ts';

const benefits = [
  {
    titleKey: 'hide_benefit_pause_title',
    descKey: 'hide_benefit_pause_desc'
  },
  {
    titleKey: 'hide_benefit_matches_title',
    descKey: 'hide_benefit_matches_desc'
  },
  {
    titleKey: 'hide_benefit_data_title',
    descKey: 'hide_benefit_data_desc'
  }
];

const HideAccount: FC = () => {
  useBackButtonToPrevRoute(routes.profileAccount, false);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const storageApi = useCloudStorage();
  const { updateProfile, hideProfile } = useProfileStore();
  const { reset: resetTooltipFlow } = useTooltipStore();
  const { isHidden } = usePolicy();

  const handleDeleteProfile = () => {
    void popup
      .show({
        title: t('delete_profile_popup_title'),
        message: t('delete_profile_popup_subtitle'),
        buttons: [
          {
            id: 'delete',
            type: 'destructive',
            text: t('confirm_delete_profile_title')
          },
          {
            id: 'cancel',
            type: 'default',
            text: t('cancel_title')
          }
        ]
      })
      .then(async (res) => {
        try {
          if (res === 'delete') {
            await ProfileController.deleteProfile();
            await updateProfile();
            resetTooltipFlow(STORAGE_KEYS.REACTION_TOOLTIP, storageApi);
            navigate(routes.index);
          }
        } catch (e) {
          catchErrors(e, true);
        }
      });
  };

  const handleHideProfile = async () => {
    if (!isHidden) {
      await hideProfile();
    }
    navigate(routes.search);
  };

  return (
    <div style={containerStyle}>
      <div style={contentWrapperStyle}>
        <div style={heroSectionStyle}>
          <div style={iconContainerStyle}>
            <PauseShape
              width={24}
              height={24}
              fillColor={UIConfig.Colors.BackgroundDark}
            />
          </div>

          <h1 style={titleStyle}>{t('hide_profile_title')}</h1>
          <p style={subtitleStyle}>{t('hide_profile_subtitle')}</p>
        </div>

        <div style={benefitsSectionStyle}>
          {benefits.map((benefit) => {
            return (
              <div key={benefit.titleKey} style={benefitCardStyle}>
                <div style={benefitTextWrapperStyle}>
                  <span style={benefitTitleStyle}>{t(benefit.titleKey)}</span>
                  <span style={benefitDescStyle}>{t(benefit.descKey)}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ flexGrow: 1 }} />

        <div style={footerNoteStyle}>
          <p style={noteTextStyle}>{t('hide_profile_switcher_subtitle')}</p>
        </div>

        <div style={buttonsContainerStyle}>
          <MainButton
            onClick={handleHideProfile}
            text={t('hide_profile_switcher_title')}
            buttonStyle={primaryButtonStyle}
          />
          <MainButton
            onClick={handleDeleteProfile}
            text={t('button.delete_profile')}
            buttonStyle={deleteButtonStyle}
          />
        </div>
      </div>
    </div>
  );
};

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  background: UIConfig.Colors.BackgroundWhite,
  position: 'relative',
  overflowX: 'hidden',
  overflowY: 'auto',
  paddingTop: '110px',
  boxSizing: 'border-box'
};

const contentWrapperStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  padding: '16px 20px',
  boxSizing: 'border-box',
  position: 'relative',
  zIndex: 1
};

const heroSectionStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  marginTop: '16px',
  marginBottom: '24px'
};

const iconContainerStyle: CSSProperties = {
  width: '56px',
  height: '56px',
  borderRadius: '16px',
  background: 'rgba(29, 29, 29, 0.06)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '14px',
  border: '1px solid rgba(0, 0, 0, 0.04)'
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: '22px',
  fontWeight: 600,
  color: UIConfig.Colors.BackgroundDark,
  fontFamily: UIConfig.Fonts.Primary
};

const subtitleStyle: CSSProperties = {
  margin: '4px 0 0 0',
  fontSize: '13px',
  color: '#8B8B8E',
  lineHeight: 1.4,
  fontFamily: UIConfig.Fonts.Primary
};

const benefitsSectionStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
};

const benefitCardStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  height: '50px',
  padding: '12px 14px',
  background: '#FFFFFF',
  borderRadius: '12px',
  border: '1px solid rgba(0, 0, 0, 0.04)'
};

const benefitTextWrapperStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  marginLeft: '12px',
  gap: '2px'
};

const benefitTitleStyle: CSSProperties = {
  fontSize: '16px',
  fontWeight: '600',
  color: '#000000',
  fontFamily: UIConfig.Fonts.Primary
};

const benefitDescStyle: CSSProperties = {
  fontSize: '13px',
  color: '#8B8B8E',
  lineHeight: 1.3,
  fontFamily: UIConfig.Fonts.Primary,
  fontWeight: '400',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden'
};

const footerNoteStyle: CSSProperties = {
  padding: '12px 14px',
  background: 'rgba(29, 29, 29, 0.04)',
  borderRadius: '10px',
  border: '1px solid rgba(0, 0, 0, 0.04)',
  marginBottom: '12px'
};

const noteTextStyle: CSSProperties = {
  margin: 0,
  fontSize: '12px',
  color: 'rgba(0, 0, 0, 0.58)',
  lineHeight: 1.4,
  fontFamily: UIConfig.Fonts.Primary
};

const buttonsContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0'
};

const primaryButtonStyle: CSSProperties = {
  background: UIConfig.Colors.BackgroundDark,
  color: '#ffffff',
  fontWeight: 600
};

const deleteButtonStyle: CSSProperties = {
  background: 'transparent',
  color: 'rgba(0, 0, 0, 0.45)',
  fontWeight: 500,
  border: 'none'
};

export default HideAccount;
